import debounce_ from "lodash/debounce";

import type { Destructor } from "~/tools/destructor";
import { fps } from "~/tools/fps";

const DEFAULT_DEBOUNCE = fps(60);

export interface OnResizeOptions<TNode> {
	node: TNode;

	/**
	 * Debounce time in milliseconds when number, or onIdle when "on-idle".
	 *
	 * @default 60fps
	 */
	debounce?: number;
}

export function onResize<TNode extends Element>(
	handler: (node: TNode, entry: ResizeObserverEntry) => void | Destructor,
	{ node, debounce = DEFAULT_DEBOUNCE }: OnResizeOptions<TNode>,
): Destructor {
	let cleanup: void | Destructor;

	if (debounce === 0) {
		const resizeObserver = new ResizeObserver(([entry]) => {
			cleanup?.();
			cleanup = handler(node, entry);
		});

		resizeObserver.observe(node);

		return () => {
			resizeObserver.disconnect();
		};
	}

	const listener = debounce_(([entry]: [ResizeObserverEntry]) => {
		cleanup?.();
		cleanup = handler(node, entry);
	}, debounce);

	const resizeObserver = new ResizeObserver(listener);

	resizeObserver.observe(node);

	return () => {
		resizeObserver.disconnect();
		listener.cancel();
		cleanup?.();
	};
}
